import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { FilterState, sortOptions, cabinClassOptions } from "./filterTypes";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

interface FilterPanelProps {
  filters: FilterState;
  onFilterChange: (newFilters: FilterState) => void;
  airlines: string[]; // Available airlines from search results
  priceRange: { min: number; max: number }; // Available price range from search results
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-50 transition-colors"
      >
        <span className="font-medium text-sm text-neutral-700">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-neutral-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-neutral-500" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  airlines,
  priceRange,
}) => {
  const handleSortChange = (sort: string) => {
    onFilterChange({ ...filters, sort: sort as FilterState["sort"] });
  };

  const handleAirlineToggle = (airline: string) => {
    const newAirlines = filters.airlines.includes(airline)
      ? filters.airlines.filter((a) => a !== airline)
      : [...filters.airlines, airline];
    onFilterChange({ ...filters, airlines: newAirlines });
  };

  const handleStopsChange = (stops: number | null) => {
    onFilterChange({ ...filters, maxStops: stops });
  };

  const handlePriceRangeChange = (values: number[]) => {
    onFilterChange({
      ...filters,
      priceRange: { min: values[0] ?? 0, max: values[1] ?? 0 },
    });
  };

  const handleCabinClassChange = (cabinClass: FilterState["cabinClass"]) => {
    onFilterChange({ ...filters, cabinClass });
  };

  const handleTimeRangeChange = (
    type: "departure" | "arrival",
    values: { start: string; end: string }
  ) => {
    onFilterChange({
      ...filters,
      [type === "departure" ? "departureTime" : "arrivalTime"]: values,
    });
  };

  return (
    <div className="bg-white">
      {/* Sort Options */}
      <CollapsibleSection title="Sort By" defaultOpen={true}>
        <div className="space-y-2">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              className={`w-full flex items-center space-x-2 p-2 rounded-md text-sm ${
                filters.sort === option.value
                  ? "bg-indigo-50 text-indigo-700"
                  : "hover:bg-neutral-50 text-neutral-700"
              }`}
            >
              <i className={`fa-solid fa-${option.icon} w-4`} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {/* Stops Filter */}
      <CollapsibleSection title="Stops">
        <div className="space-y-2">
          {[null, 0, 1, 2].map((stops) => (
            <button
              key={stops === null ? "any" : stops}
              onClick={() => handleStopsChange(stops)}
              className={`w-full flex items-center justify-between p-2 rounded-md text-sm ${
                filters.maxStops === stops
                  ? "bg-indigo-50 text-indigo-700"
                  : "hover:bg-neutral-50 text-neutral-700"
              }`}
            >
              <span>
                {stops === null
                  ? "Any number of stops"
                  : stops === 0
                  ? "Non-stop only"
                  : `Up to ${stops} stop${stops > 1 ? "s" : ""}`}
              </span>
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {/* Airlines Filter */}
      <CollapsibleSection title="Airlines">
        <div className="space-y-3">
          {/* Select/Clear All */}
          <div className="flex items-center justify-between px-2">
            <button
              onClick={() => onFilterChange({ ...filters, airlines: airlines })}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              Select All
            </button>
            <button
              onClick={() => onFilterChange({ ...filters, airlines: [] })}
              className="text-xs font-medium text-neutral-500 hover:text-neutral-700"
            >
              Clear All
            </button>
          </div>

          {/* Airlines List */}
          <div className="space-y-2">
            {airlines.map((airline) => (
              <label
                key={airline}
                className="flex items-center justify-between p-2 hover:bg-neutral-50 rounded-md cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={filters.airlines.includes(airline)}
                    onChange={() => handleAirlineToggle(airline)}
                    className="h-4 w-4 rounded border-neutral-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <span className="text-sm text-neutral-700">{airline}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Price Range Filter */}
      <CollapsibleSection title="Price Range">
        <div className="px-2 pt-6">
          <Slider
            defaultValue={[priceRange.min, priceRange.max]}
            min={priceRange.min}
            max={priceRange.max}
            step={10}
            onValueChange={handlePriceRangeChange}
          />
          <div className="flex justify-between mt-2 text-sm text-neutral-600">
            <span>${filters.priceRange?.min || priceRange.min}</span>
            <span>${filters.priceRange?.max || priceRange.max}</span>
          </div>
        </div>
      </CollapsibleSection>

      {/* Cabin Class Filter */}
      <CollapsibleSection title="Cabin Class">
        <div className="space-y-2">
          {cabinClassOptions.map((option) => (
            <button
              key={option.value}
              onClick={() =>
                handleCabinClassChange(
                  option.value as FilterState["cabinClass"]
                )
              }
              className={`w-full flex items-center justify-between p-2 rounded-md text-sm ${
                filters.cabinClass === option.value
                  ? "bg-indigo-50 text-indigo-700"
                  : "hover:bg-neutral-50 text-neutral-700"
              }`}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {/* Active Filters */}
      <div className="p-3">
        <div className="flex flex-wrap gap-2">
          {filters.airlines.length > 0 && (
            <Badge
              variant="secondary"
              className="gap-2 py-1.5 px-3 font-medium"
            >
              Airlines ({filters.airlines.length})
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => onFilterChange({ ...filters, airlines: [] })}
              />
            </Badge>
          )}
          {filters.maxStops !== null && (
            <Badge
              variant="secondary"
              className="gap-2 py-1.5 px-3 font-medium"
            >
              Max {filters.maxStops} stop{filters.maxStops !== 1 && "s"}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => onFilterChange({ ...filters, maxStops: null })}
              />
            </Badge>
          )}
          {filters.cabinClass && (
            <Badge
              variant="secondary"
              className="gap-2 py-1.5 px-3 font-medium"
            >
              {
                cabinClassOptions.find((o) => o.value === filters.cabinClass)
                  ?.label
              }
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => onFilterChange({ ...filters, cabinClass: null })}
              />
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
